"use client";

import {
  FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Link from "next/link";
import {
  Building2,
  CarFront,
  LoaderCircle,
  ChevronLeft,
  ChevronRight,
  Heart,
  MapPin,
  Search,
  ShoppingBag,
  SlidersHorizontal,
  X,
  Wrench,
} from "lucide-react";

import Footer from "@/components/layout/Footer";
import {
  trackMarketplaceClickOncePerSession,
  trackMarketplaceImpressionOncePerSession,
} from "@/lib/analytics";
import {
  getMarketplaceItems,
  normalizeMarketplaceParams,
  type MarketplaceResponse,
  type MarketplaceItem,
  type MarketplaceType,
} from "@/lib/marketplace";

import styles from "./marketplace.module.css";

type Filter = "all" | MarketplaceType;

const filters: Array<{
  value: Filter;
  label: string;
  icon: typeof ShoppingBag;
}> = [
  {
    value: "all",
    label: "Todos",
    icon: Search,
  },
  {
    value: "products",
    label: "Produtos",
    icon: ShoppingBag,
  },
  {
    value: "services",
    label: "Serviços",
    icon: Wrench,
  },
  {
    value: "vehicles",
    label: "Veículos",
    icon: CarFront,
  },
  {
    value: "real_estate",
    label: "Imóveis",
    icon: Building2,
  },
];

function formatCurrency(value: string) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return value;
  }

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(number);
}

function getItemLabel(type: MarketplaceType) {
  if (type === "services") return "Serviço";
  if (type === "vehicles") return "Veículo";
  if (type === "real_estate") return "Imóvel";
  return "Produto";
}

function getMarketplaceDetailPath(
  item: MarketplaceItem,
) {
  const itemType =
    item.marketplace_type ===
    "real_estate"
      ? "imovel"
      : item.marketplace_type ===
          "vehicles"
        ? "veiculo"
        : item.marketplace_type ===
            "services"
          ? "servico"
          : "produto";

  return (
    `/marketplace/${itemType}/` +
    `${item.company.slug}/${item.slug}`
  );
}

function MarketplaceTrackedLink({
  item,
  className,
  children,
}: {
  item: MarketplaceItem;
  className: string;
  children: ReactNode;
}) {
  const linkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const element = linkRef.current;

    if (!element) {
      return;
    }

    if (
      typeof IntersectionObserver === "undefined"
    ) {
      trackMarketplaceImpressionOncePerSession(
        item.company.slug,
        item.id,
        item.promotion,
      );
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (
          entry?.isIntersecting &&
          entry.intersectionRatio >= 0.35
        ) {
          trackMarketplaceImpressionOncePerSession(
            item.company.slug,
            item.id,
            item.promotion,
          );

          observer.disconnect();
        }
      },
      {
        threshold: [0.35],
      },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [item.company.slug, item.id, item.promotion]);

  return (
    <Link
      ref={linkRef}
      href={getMarketplaceDetailPath(item) + (item.promotion
        ? `?campaign=${encodeURIComponent(item.promotion.campaign_id)}&placement=${encodeURIComponent(item.promotion.campaign_placement)}`
        : "")}
      className={className}
      onClick={() =>
        trackMarketplaceClickOncePerSession(
          item.company.slug,
          item.id,
          item.promotion,
        )
      }
    >
      {children}
    </Link>
  );
}

function getLocation(item: MarketplaceItem) {
  return [
    item.marketplace_type === "real_estate" ? getBusinessString(item, "city") || item.company.city : item.company.city,
    item.marketplace_type === "real_estate" ? getBusinessString(item, "state") || item.company.state : item.company.state,
  ]
    .filter(Boolean)
    .join(" - ");
}

function getBusinessString(
  item: MarketplaceItem,
  key: string,
) {
  const value =
    item.business_attributes?.[key];

  return typeof value === "string"
    ? value.trim()
    : "";
}

function getPropertyTypeLabel(
  value: string,
) {
  const labels: Record<string, string> = {
    apartment: "Apartamento",
    house: "Casa",
    land: "Terreno",
    commercial: "Comercial",
    studio: "Studio",
    farm: "Rural",
  };

  return labels[value] ?? value;
}

export default function MarketplaceClient() {
  const [inputValue, setInputValue] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilterState] = useState<Filter>("all");

  const vehicleQuery = inputValue;
  const setVehicleQuery = setInputValue;

  const [vehicleBrand, setVehicleBrand] =
    useState("");

  const [vehicleModel, setVehicleModel] =
    useState("");

  const [vehicleYear, setVehicleYear] =
    useState("");

  const [
    vehicleMinPrice,
    setVehicleMinPrice,
  ] = useState("");

  const [
    vehicleMaxPrice,
    setVehicleMaxPrice,
  ] = useState("");

  const realEstateQuery = inputValue;
  const setRealEstateQuery = setInputValue;

  const [
    realEstateTransaction,
    setRealEstateTransaction,
  ] = useState<"" | "sale" | "rent">("");

  const [
    realEstateNeighborhood,
    setRealEstateNeighborhood,
  ] = useState("");

  const [
    realEstatePropertyType,
    setRealEstatePropertyType,
  ] = useState("");
  const [selectedCategory, setSelectedCategoryState] =
    useState("");
  const [categories, setCategories] =
    useState<string[]>([]);
  const [items, setItems] = useState<MarketplaceItem[]>([]);
  const [count, setCount] = useState(0);

  const [homeSections, setHomeSections] = useState<{
    mostViewed: MarketplaceItem[];
    featured: MarketplaceItem[];
    novidades: MarketplaceItem[];
    vehicles: MarketplaceItem[];
    realEstate: MarketplaceItem[];
    products: MarketplaceItem[];
    services: MarketplaceItem[];
  }>({
    mostViewed: [],
    featured: [],
    novidades: [],
    vehicles: [],
    realEstate: [],
    products: [],
    services: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [appliedQuery, setAppliedQuery] = useState<string | null>(null);
  const [sort, setSort] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [subcategories, setSubcategories] = useState<string[]>([]);
  const [facets, setFacets] = useState<MarketplaceResponse["facets"]>({});
  const requestVersion = useRef(0);
  const filterDialogRef = useRef<HTMLDialogElement>(null);
  const hasGlobalFilters = filter === "all" || filter === "products" || filter === "services";
  const activeFilterCount = ["search", "sort", "city", "state", "min", "max", "category", "subcategory"]
    .filter((key) => new URLSearchParams(appliedQuery ?? "").has(key)).length;

  useEffect(() => {
    filterDialogRef.current?.close();
  }, [filter]);

  const restoreFilters = useCallback((params: URLSearchParams) => {
    const normalized = normalizeMarketplaceParams(params);
    setAppliedQuery(normalized.toString());
    setFilterState((normalized.get("type") ?? "all") as Filter);
    setInputValue(normalized.get("search") ?? "");
    setSearch(normalized.get("search") ?? "");
    setSort(normalized.get("sort") ?? "");
    setCity(normalized.get("city") ?? "");
    setState(normalized.get("state") ?? "");
    setVehicleMinPrice(normalized.get("min") ?? "");
    setVehicleMaxPrice(normalized.get("max") ?? "");
    setVehicleBrand(normalized.get("brand") ?? "");
    setVehicleModel(normalized.get("model") ?? "");
    setVehicleYear(normalized.get("year") ?? "");
    setRealEstateTransaction((normalized.get("transaction_type") ?? "") as "" | "sale" | "rent");
    setRealEstateNeighborhood(normalized.get("neighborhood") ?? "");
    setRealEstatePropertyType(normalized.get("property_type") ?? "");
    setSelectedCategoryState(normalized.get("category") ?? "");
    setSubcategory(normalized.get("subcategory") ?? "");
  }, []);

  useEffect(() => {
    const restore = () => {
      const params = normalizeMarketplaceParams(new URLSearchParams(window.location.search));
      const query = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (query ? "?" + query : "") + window.location.hash);
      requestVersion.current += 1;
      restoreFilters(params);
    };
    restore();
    window.addEventListener("popstate", restore);
    return () => { window.removeEventListener("popstate", restore); requestVersion.current += 1; };
  }, [restoreFilters]);

  function updateFilters(params: URLSearchParams) {
    const normalized = normalizeMarketplaceParams(params);
    const query = normalized.toString();
    if (query !== appliedQuery) {
      requestVersion.current += 1;
      window.history.pushState(null, "", window.location.pathname + (query ? "?" + query : ""));
    }
    restoreFilters(normalized);
  }

  function applyFilters(overrides: Record<string, string> = {}) {
    const params = new URLSearchParams(appliedQuery ?? "");
    const values: Record<string, string> = {
      type: filter, search: inputValue, sort, city, state,
      min: vehicleMinPrice, max: vehicleMaxPrice,
      brand: vehicleBrand, model: vehicleModel, year: vehicleYear,
      transaction_type: realEstateTransaction, neighborhood: realEstateNeighborhood,
      property_type: realEstatePropertyType, category: selectedCategory, subcategory,
      ...overrides,
    };
    for (const [key, value] of Object.entries(values)) {
      if (value.trim()) params.set(key, value.trim()); else params.delete(key);
    }
    updateFilters(params);
  }

  function setFilter(value: Filter) {
    const params = new URLSearchParams(appliedQuery ?? "");
    for (const key of ["brand", "model", "year", "transaction_type", "neighborhood", "property_type", "category", "subcategory"]) params.delete(key);
    if (value === "all") params.delete("type"); else params.set("type", value);
    updateFilters(params);
  }

  function setSelectedCategory(value: string) {
    applyFilters({ category: value, subcategory: "" });
  }

  function clearFilters() {
    const params = new URLSearchParams();
    if (filter !== "all") params.set("type", filter);
    updateFilters(params);
  }

  const isMarketplaceHome = appliedQuery === "";
  const hasAppliedFilters = Boolean(appliedQuery && [...new URLSearchParams(appliedQuery).keys()].some((key) => key !== "type"));

  const loadItems = useCallback(async () => {
    if (appliedQuery === null) return;
    const version = ++requestVersion.current;
    try {
      setLoading(true);
      setError("");

      if (isMarketplaceHome) {
        const makeParams = (
          type?: MarketplaceType,
          sort?: string,
        ) => {
          const params = new URLSearchParams();

          params.set("limit", "6");

          if (type) {
            params.set("type", type);
          }

          if (sort) {
            params.set("sort", sort);
          }

          return params;
        };

        const [
          mostViewed,
          featured,
          novidades,
          vehicles,
          realEstate,
          products,
          services,
        ] = await Promise.all([
          getMarketplaceItems(
            makeParams(undefined, "most_viewed"),
          ),
          getMarketplaceItems(
            (() => {
              const params =
                new URLSearchParams();

              params.set("limit", "6");
              params.set("featured", "1");
              params.set("placement", "marketplace_featured");

              return params;
            })(),
          ),
          getMarketplaceItems(
            (() => {
              const params =
                new URLSearchParams();

              params.set("limit", "18");
              params.set("sort", "recent");

              return params;
            })(),
          ),
          getMarketplaceItems(
            makeParams("vehicles"),
          ),
          getMarketplaceItems(
            makeParams("real_estate"),
          ),
          getMarketplaceItems(
            makeParams("products"),
          ),
          getMarketplaceItems(
            makeParams("services"),
          ),
        ]);

        if (version !== requestVersion.current) return;
        setHomeSections({
          mostViewed: mostViewed.results,
          featured: featured.results,
          novidades: novidades.results,
          vehicles: vehicles.results,
          realEstate: realEstate.results,
          products: products.results,
          services: services.results,
        });

        setItems([]);
        setCategories([]);
        setCount(featured.count);
        return;
      }

      const params = new URLSearchParams(appliedQuery);
      if (!params.has("limit")) params.set("limit", filter === "vehicles" || filter === "real_estate" ? "100" : "60");

      const data = await getMarketplaceItems(params);

      if (version !== requestVersion.current) return;
      setItems(data.results);
      setFacets(data.facets ?? {});
      setSubcategories(data.subcategories ?? []);
      setCount(data.count);
      setCategories(
        data.categories ?? [],
      );
    } catch {
      if (version !== requestVersion.current) return;
      setItems([]);
      setCategories([]);
      setCount(0);
      setError(
        "Não foi possível carregar o Marketplace. Tente novamente.",
      );
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [appliedQuery, filter, isMarketplaceHome]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  function submitSearch(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();
    applyFilters();
  }

  const novidadesRef =
    useRef<HTMLDivElement>(null);

  const novidadesDraggingRef =
    useRef(false);

  const novidadesDragMovedRef =
    useRef(false);

  const novidadesDragStartRef =
    useRef(0);

  const novidadesScrollStartRef =
    useRef(0);

  const handleNovidadesPointerDown = (
    event: React.PointerEvent<HTMLDivElement>,
  ) => {
    if (event.pointerType === "touch") {
      return;
    }

    const element = novidadesRef.current;

    if (!element) {
      return;
    }

    novidadesDraggingRef.current = true;
    novidadesDragMovedRef.current = false;
    novidadesDragStartRef.current =
      event.clientX;
    novidadesScrollStartRef.current =
      element.scrollLeft;

    element.setPointerCapture(
      event.pointerId,
    );
  };

  const handleNovidadesPointerMove = (
    event: React.PointerEvent<HTMLDivElement>,
  ) => {
    if (
      !novidadesDraggingRef.current ||
      event.pointerType === "touch"
    ) {
      return;
    }

    const element = novidadesRef.current;

    if (!element) {
      return;
    }

    const delta =
      event.clientX -
      novidadesDragStartRef.current;

    if (Math.abs(delta) > 5) {
      novidadesDragMovedRef.current = true;
    }

    element.scrollLeft =
      novidadesScrollStartRef.current -
      delta;
  };

  const handleNovidadesPointerEnd = (
    event: React.PointerEvent<HTMLDivElement>,
  ) => {
    if (event.pointerType === "touch") {
      return;
    }

    novidadesDraggingRef.current = false;

    const element = novidadesRef.current;

    if (
      element?.hasPointerCapture(
        event.pointerId,
      )
    ) {
      element.releasePointerCapture(
        event.pointerId,
      );
    }
  };

  const handleNovidadesClickCapture = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (!novidadesDragMovedRef.current) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    novidadesDragMovedRef.current = false;
  };

  const scrollNovidades = (
    direction: "left" | "right",
  ) => {
    const element = novidadesRef.current;

    if (!element) {
      return;
    }

    const amount =
      element.clientWidth * 0.82;

    element.scrollBy({
      left:
        direction === "right"
          ? amount
          : -amount,
      behavior: "smooth",
    });
  };

  function renderVehicleMarketplace() {
    const vehicles = items.filter(
      (item) =>
        item.marketplace_type ===
        "vehicles",
    );

    const brandGroups =
      new Map<
        string,
        {
          name: string;
          count: number;
          image: string | null;
        }
      >();

    for (const vehicle of vehicles) {
      const brand =
        getBusinessString(
          vehicle,
          "brand",
        );

      if (!brand) {
        continue;
      }

      const current =
        brandGroups.get(brand);

      if (current) {
        current.count += 1;

        if (
          !current.image &&
          vehicle.primary_image_url
        ) {
          current.image =
            vehicle.primary_image_url;
        }

        continue;
      }

      brandGroups.set(
        brand,
        {
          name: brand,
          count: 1,
          image:
            vehicle.primary_image_url,
        },
      );
    }

    const brands = [
      ...brandGroups.values(),
    ].sort((a, b) =>
      a.name.localeCompare(
        b.name,
        "pt-BR",
      ),
    );

    const models = [...new Set([vehicleModel, ...(facets.models ?? [])].filter(Boolean))];
    const years = [...new Set([...(vehicleYear ? [Number(vehicleYear)] : []), ...(facets.years ?? [])])];
    const brandOptions = [...new Set([vehicleBrand, ...(facets.brands ?? brands.map((brand) => brand.name))].filter(Boolean))];
    const filteredVehicles = vehicles;
    const hasVehicleFilters = hasAppliedFilters;

    const featuredVehicles =
      vehicles.filter(
        (vehicle) =>
          vehicle.is_featured,
      );

    const clearVehicleFilters = clearFilters;

    return (
      <div
        className={
          styles.vehicleMarketplaceHome
        }
      >
        <section
          className={
            styles.vehicleSearchPanel
          }
        >
          <div
            className={
              styles.vehicleSearchHeading
            }
          >
            <span>
              Marketplace automotivo
            </span>

            <h2>
              Qual veículo você procura?
            </h2>

            <p>
              Encontre veículos anunciados
              por lojas e profissionais no
              Fornecedor.Net.
            </p>
          </div>

          <div
            className={
              styles.vehicleSearchGrid
            }
          >
            <label
              className={
                styles.vehicleQueryField
              }
            >
              <span>
                Buscar veículo
              </span>

              <div>
                <Search size={18} />

                <input
                  type="search"
                  value={vehicleQuery}
                  onChange={(event) =>
                    setVehicleQuery(
                      event.target.value,
                    )
                  }
                  placeholder="Nome, versão ou referência"
                />
              </div>
            </label>

            <label>
              <span>Marca</span>

              <select
                value={vehicleBrand}
                onChange={(event) => {
                  applyFilters({brand: event.target.value, model: ""});
                }}
              >
                <option value="">
                  Todas as marcas
                </option>

                {brandOptions.map(
                  (brand) => (
                    <option
                      key={brand}
                      value={brand}
                    >
                      {brand}
                    </option>
                  ),
                )}
              </select>
            </label>

            <label>
              <span>Modelo</span>

              <select
                value={vehicleModel}
                onChange={(event) =>
                  setVehicleModel(
                    event.target.value,
                  )
                }
              >
                <option value="">
                  Todos os modelos
                </option>

                {models.map(
                  (model) => (
                    <option
                      key={model}
                      value={model}
                    >
                      {model}
                    </option>
                  ),
                )}
              </select>
            </label>

            <label>
              <span>Ano</span>

              <select
                value={vehicleYear}
                onChange={(event) =>
                  setVehicleYear(
                    event.target.value,
                  )
                }
              >
                <option value="">
                  Todos os anos
                </option>

                {years.map(
                  (year) => (
                    <option
                      key={year}
                      value={String(year)}
                    >
                      {year}
                    </option>
                  ),
                )}
              </select>
            </label>

            <label>
              <span>
                Preço mínimo
              </span>

              <input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                placeholder="Sem mínimo"
                value={vehicleMinPrice}
                onChange={(event) =>
                  setVehicleMinPrice(
                    event.target.value,
                  )
                }
              />
            </label>

            <label>
              <span>
                Preço máximo
              </span>

              <input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                placeholder="Sem máximo"
                value={vehicleMaxPrice}
                onChange={(event) =>
                  setVehicleMaxPrice(
                    event.target.value,
                  )
                }
              />
            </label>
          </div>

          <div className={styles.vehicleSearchFooter}><button type="button" onClick={() => applyFilters()}>Aplicar filtros</button></div>

          {hasVehicleFilters && (
            <div
              className={
                styles.vehicleSearchFooter
              }
            >
              <span>
                {
                  count
                }{" "}
                {count ===
                1
                  ? "veículo encontrado"
                  : "veículos encontrados"}
              </span>

              <button
                type="button"
                onClick={
                  clearVehicleFilters
                }
              >
                Limpar filtros
              </button>
            </div>
          )}
        </section>

        {!hasVehicleFilters &&
          brands.length > 0 && (
            <section
              className={
                styles.vehicleBrands
              }
            >
              <div
                className={
                  styles.sectionHeader
                }
              >
                <div>
                  <span
                    className={
                      styles
                        .resultEyebrow
                    }
                  >
                    Encontre sua marca
                  </span>

                  <h2>
                    Explore por marca
                  </h2>

                  <p>
                    Veja os veículos
                    disponíveis por
                    fabricante.
                  </p>
                </div>
              </div>

              <div
                className={
                  styles.vehicleBrandTrack
                }
              >
                {brands.map(
                  (brand) => (
                    <button
                      key={brand.name}
                      type="button"
                      className={
                        styles
                          .vehicleBrandCard
                      }
                      onClick={() => {
                        applyFilters({brand: brand.name, model: ""});
                      }}
                    >
                      {brand.image ? (
                        <img
                          src={brand.image}
                          alt=""
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className={
                            styles
                              .vehicleBrandFallback
                          }
                        >
                          <CarFront
                            size={36}
                          />
                        </div>
                      )}

                      <span
                        className={
                          styles
                            .vehicleBrandOverlay
                        }
                      />

                      <span
                        className={
                          styles
                            .vehicleBrandContent
                        }
                      >
                        <strong>
                          {brand.name}
                        </strong>

                        <small>
                          {brand.count}{" "}
                          {brand.count === 1
                            ? "veículo"
                            : "veículos"}
                        </small>
                      </span>
                    </button>
                  ),
                )}
              </div>
            </section>
          )}

        {hasVehicleFilters ? (
          renderSection(
            "Resultados da busca",
            filteredVehicles,
            undefined,
            `${count} ${count === 1 ? "veículo encontrado" : "veículos encontrados"}`,
          )
        ) : (
          <>
            {featuredVehicles.length >
              0 &&
              renderSection(
                "Veículos em destaque",
                featuredVehicles.slice(
                  0,
                  6,
                ),
                undefined,
                "Uma seleção de veículos em evidência.",
              )}

            {renderSection(
              "Veículos disponíveis",
              vehicles.slice(0, 6),
              undefined,
              "Confira algumas opções disponíveis agora.",
            )}
          </>
        )}
      </div>
    );
  }

  function renderRealEstateMarketplace() {
    const properties = items.filter(
      (item) =>
        item.marketplace_type ===
        "real_estate",
    );

    const neighborhoodsMap =
      new Map<
        string,
        {
          name: string;
          count: number;
          image: string | null;
        }
      >();

    for (const property of properties) {
      const neighborhood =
        getBusinessString(
          property,
          "neighborhood",
        );

      if (!neighborhood) {
        continue;
      }

      const current =
        neighborhoodsMap.get(
          neighborhood,
        );

      if (current) {
        current.count += 1;

        if (
          !current.image &&
          property.primary_image_url
        ) {
          current.image =
            property.primary_image_url;
        }

        continue;
      }

      neighborhoodsMap.set(
        neighborhood,
        {
          name: neighborhood,
          count: 1,
          image:
            property.primary_image_url,
        },
      );
    }

    const neighborhoods = [
      ...neighborhoodsMap.values(),
    ].sort((a, b) =>
      a.name.localeCompare(
        b.name,
        "pt-BR",
      ),
    );

    const propertyTypes = [...new Set([realEstatePropertyType, ...(facets.property_types ?? [])].filter(Boolean))];
    const neighborhoodOptions = [...new Set([realEstateNeighborhood, ...(facets.neighborhoods ?? neighborhoods.map((region) => region.name))].filter(Boolean))];
    const filteredProperties = properties;
    const hasRealEstateFilters = hasAppliedFilters;

    const saleProperties =
      properties.filter(
        (property) =>
          getBusinessString(
            property,
            "transaction_type",
          ) === "sale",
      );

    const rentProperties =
      properties.filter(
        (property) =>
          getBusinessString(
            property,
            "transaction_type",
          ) === "rent",
      );

    const clearRealEstateFilters = clearFilters;

    return (
      <div
        className={
          styles.realEstateHome
        }
      >
        <section
          className={
            styles.realEstateSearch
          }
        >
          <div
            className={
              styles.realEstateSearchHeading
            }
          >
            <span>
              Marketplace imobiliário
            </span>

            <h2>
              Encontre seu próximo imóvel
            </h2>

            <p>
              Busque entre imóveis de
              imobiliárias e profissionais
              presentes no Fornecedor.Net.
            </p>
          </div>

          <div
            className={
              styles.realEstateTransactions
            }
          >
            {[
              {
                value: "",
                label: "Todos",
              },
              {
                value: "sale",
                label: "Comprar",
              },
              {
                value: "rent",
                label: "Alugar",
              },
            ].map((option) => (
              <button
                key={
                  option.value ||
                  "all"
                }
                type="button"
                className={
                  realEstateTransaction ===
                  option.value
                    ? styles
                        .realEstateTransactionActive
                    : styles
                        .realEstateTransaction
                }
                onClick={() =>
                  applyFilters({transaction_type: option.value})
                }
              >
                {option.label}
              </button>
            ))}
          </div>

          <div
            className={
              styles.realEstateSearchGrid
            }
          >
            <label
              className={
                styles.realEstateQuery
              }
            >
              <span>
                O que você procura?
              </span>

              <div>
                <Search size={18} />

                <input
                  value={realEstateQuery}
                  onChange={(event) =>
                    setRealEstateQuery(
                      event.target.value,
                    )
                  }
                  placeholder="Apartamento, casa, bairro..."
                />
              </div>
            </label>

            <label>
              <span>Bairro</span>

              <select
                value={
                  realEstateNeighborhood
                }
                onChange={(event) =>
                  setRealEstateNeighborhood(
                    event.target.value,
                  )
                }
              >
                <option value="">
                  Todos os bairros
                </option>

                {neighborhoodOptions.map(
                  (region) => (
                    <option
                      key={region}
                      value={region}
                    >
                      {region}
                    </option>
                  ),
                )}
              </select>
            </label>

            <label>
              <span>
                Tipo de imóvel
              </span>

              <select
                value={
                  realEstatePropertyType
                }
                onChange={(event) =>
                  setRealEstatePropertyType(
                    event.target.value,
                  )
                }
              >
                <option value="">
                  Todos os tipos
                </option>

                {propertyTypes.map(
                  (type) => (
                    <option
                      key={type}
                      value={type}
                    >
                      {getPropertyTypeLabel(
                        type,
                      )}
                    </option>
                  ),
                )}
              </select>
            </label>
          </div>

          <div className={styles.realEstateSearchFooter}><button type="button" onClick={() => applyFilters()}>Aplicar filtros</button></div>

          {hasRealEstateFilters && (
            <div
              className={
                styles.realEstateSearchFooter
              }
            >
              <span>
                {
                  count
                }{" "}
                {count ===
                1
                  ? "imóvel encontrado"
                  : "imóveis encontrados"}
              </span>

              <button
                type="button"
                onClick={
                  clearRealEstateFilters
                }
              >
                Limpar filtros
              </button>
            </div>
          )}
        </section>

        {!hasRealEstateFilters &&
          neighborhoods.length > 0 && (
            <section
              className={
                styles.realEstateRegions
              }
            >
              <div
                className={
                  styles.sectionHeader
                }
              >
                <div>
                  <span
                    className={
                      styles
                        .resultEyebrow
                    }
                  >
                    Encontre o seu lugar
                  </span>

                  <h2>
                    Explore por bairro
                  </h2>

                  <p>
                    Veja onde estão os
                    imóveis disponíveis.
                  </p>
                </div>
              </div>

              <div
                className={
                  styles.realEstateRegionTrack
                }
              >
                {neighborhoods.map(
                  (region) => (
                    <button
                      key={region.name}
                      type="button"
                      className={
                        styles
                          .realEstateRegionCard
                      }
                      onClick={() =>
                        applyFilters({neighborhood: region.name})
                      }
                    >
                      {region.image ? (
                        <img
                          src={
                            region.image
                          }
                          alt=""
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className={
                            styles
                              .realEstateRegionFallback
                          }
                        >
                          <MapPin
                            size={34}
                          />
                        </div>
                      )}

                      <span
                        className={
                          styles
                            .realEstateRegionOverlay
                        }
                      />

                      <span
                        className={
                          styles
                            .realEstateRegionContent
                        }
                      >
                        <strong>
                          {region.name}
                        </strong>

                        <small>
                          {region.count}{" "}
                          {region.count === 1
                            ? "imóvel"
                            : "imóveis"}
                        </small>
                      </span>
                    </button>
                  ),
                )}
              </div>
            </section>
          )}

        {hasRealEstateFilters ? (
          renderSection(
            "Resultados da busca",
            filteredProperties,
            undefined,
            `${count} ${count === 1 ? "imóvel encontrado" : "imóveis encontrados"}`,
          )
        ) : (
          <>
            {renderSection(
              "Imóveis para comprar",
              saleProperties.slice(0, 6),
              undefined,
              "Encontre imóveis disponíveis para venda.",
            )}

            {renderSection(
              "Imóveis para alugar",
              rentProperties.slice(0, 6),
              undefined,
              "Veja as opções disponíveis para locação.",
            )}
          </>
        )}
      </div>
    );
  }

  function renderNovidades() {
    if (homeSections.novidades.length === 0) {
      return null;
    }

    return (
      <section className={styles.novidadesSection}>
        <div className={styles.novidadesHeader}>
          <div>
            <span
              className={styles.novidadesEyebrow}
            >
              Acabou de chegar
            </span>

            <h2>Novidades</h2>

            <p>
              Veja o que acabou de entrar no
              Fornecedor.Net.
            </p>
          </div>

          <div
            className={styles.carouselControls}
          >
            <button
              type="button"
              aria-label="Novidades anteriores"
              onClick={() =>
                scrollNovidades("left")
              }
            >
              <ChevronLeft size={20} />
            </button>

            <button
              type="button"
              aria-label="Próximas novidades"
              onClick={() =>
                scrollNovidades("right")
              }
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div
          ref={novidadesRef}
          className={styles.novidadesCarousel}
          onPointerDown={
            handleNovidadesPointerDown
          }
          onPointerMove={
            handleNovidadesPointerMove
          }
          onPointerUp={
            handleNovidadesPointerEnd
          }
          onPointerCancel={
            handleNovidadesPointerEnd
          }
          onClickCapture={
            handleNovidadesClickCapture
          }
        >
          {homeSections.novidades.map(
            (item) => {
              const location =
                getLocation(item);

              return (
                <MarketplaceTrackedLink
                  key={item.id}
                  item={item}
                  className={
                    styles.novidadeCard
                  }
                >
                  <div
                    className={
                      styles.novidadeImage
                    }
                  >
                    {item.primary_image_url ? (
                      <img
                        src={
                          item.primary_image_url
                        }
                        alt={item.name}
                        loading="lazy"
                      />
                    ) : (
                      <div
                        className={
                          styles
                            .imagePlaceholder
                        }
                      >
                        <ShoppingBag
                          size={34}
                        />
                      </div>
                    )}

                    <span
                      className={
                        styles.favoriteVisual
                      }
                      aria-hidden="true"
                    >
                      <Heart size={18} />
                    </span>

                    <span
                      className={
                        styles.novidadeBadge
                      }
                    >
                      Novidade
                    </span>

                    <span
                      className={
                        styles.novidadeType
                      }
                    >
                      {getItemLabel(
                        item.marketplace_type,
                      )}
                    </span>
                  </div>

                  <div
                    className={
                      styles.novidadeBody
                    }
                  >
                    <span
                      className={
                        styles.novidadeCompany
                      }
                    >
                      {item.company.trade_name}
                    </span>

                    <h3>{item.name}</h3>

                    <strong>
                      {formatCurrency(
                        item.current_price,
                      )}
                    </strong>

                    {location && (
                      <div
                        className={
                          styles.novidadeLocation
                        }
                      >
                        <MapPin size={14} />
                        <span>{location}</span>
                      </div>
                    )}
                  </div>
                </MarketplaceTrackedLink>
              );
            },
          )}
        </div>
      </section>
    );
  }

  function renderSection(
    title: string,
    sectionItems: MarketplaceItem[],
    targetFilter?: MarketplaceType,
    subtitle?: string,
  ) {
    if (sectionItems.length === 0) {
      if (title === "Resultados da busca") {
        return <div className={styles.state}><Search size={38} /><h3>Nenhum anúncio encontrado</h3><p>Tente alterar a busca ou os filtros.</p></div>;
      }
      return null;
    }

    return (
      <section className={styles.marketSection}>
        <div className={styles.sectionHeader}>
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>

          {targetFilter && (
            <button
              type="button"
              className={styles.seeAll}
              onClick={() => setFilter(targetFilter)}
            >
              Ver todos
            </button>
          )}
        </div>

        <div className={styles.grid}>
          {sectionItems.map((item) => {
            const location = getLocation(item);

            return (
              <MarketplaceTrackedLink
                key={item.id}
                item={item}
                className={styles.card}
              >
                <div className={styles.imageWrap}>
                  {item.primary_image_url ? (
                    <img
                      src={item.primary_image_url}
                      alt={item.name}
                      loading="lazy"
                    />
                  ) : (
                    <div
                      className={
                        styles.imagePlaceholder
                      }
                    >
                      <ShoppingBag size={34} />
                    </div>
                  )}

                  <span
                    className={styles.favoriteVisual}
                    aria-hidden="true"
                  >
                    <Heart size={18} />
                  </span>

                  <span className={styles.typeBadge}>
                    {item.promotion?.sponsored ? "Patrocinado" : getItemLabel(
                      item.marketplace_type,
                    )}
                  </span>
                </div>

                <div className={styles.cardBody}>
                  <div className={styles.company}>
                    <span>
                      {item.company.trade_name}
                    </span>

                    {item.company.is_verified && (
                      <span
                        className={styles.verified}
                        title="Empresa verificada"
                      >
                        ✓
                      </span>
                    )}
                  </div>

                  <h3>{item.name}</h3>

                  {item.reference_code && (
                    <span className={styles.reference}>
                      Ref. {item.reference_code}
                    </span>
                  )}

                  <div className={styles.price}>
                    {formatCurrency(
                      item.current_price,
                    )}
                  </div>

                  {location && (
                    <div className={styles.location}>
                      <MapPin size={15} />
                      <span>{location}</span>
                    </div>
                  )}
                </div>
              </MarketplaceTrackedLink>
            );
          })}
        </div>
      </section>
    );
  }

  return (
    <>
      <main className={styles.page}>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <span className={styles.eyebrow}>
              Marketplace Fornecedor.Net
            </span>

            <h1>
              Encontre o que você procura.
            </h1>

            <p>
              Produtos, serviços, veículos e imóveis de
              empresas reunidos em um só lugar.
            </p>

          </div>
        </section>

        <section className={styles.content}>
          <div
            className={styles.filters}
            aria-label="Tipos de anúncio"
          >
            {filters.map(
              ({
                value,
                label,
                icon: Icon,
              }) => (
                <button
                  key={value}
                  type="button"
                  className={
                    filter === value
                      ? styles.filterActive
                      : styles.filter
                  }
                  onClick={() => setFilter(value)}
                >
                  <Icon size={18} />
                  {label}
                </button>
              ),
            )}
          </div>

          {hasGlobalFilters && (
            <>
              <div className={`${styles.resultHeader} ${styles.filterResultsHeader}`}>
                <div>
                  <span className={styles.resultEyebrow}>Anúncios</span>
                  <h2>{search ? `Resultados para “${search}”` : "Explore o Marketplace"}</h2>
                  {!isMarketplaceHome && <span className={styles.count}>{count} {count === 1 ? "anúncio encontrado" : "anúncios encontrados"}</span>}
                </div>
                <button
                  type="button"
                  className={styles.filterToggle}
                  aria-label={activeFilterCount ? `Filtros, ${activeFilterCount} ativos` : "Filtros"}
                  aria-haspopup="dialog"
                  aria-controls="marketplace-filter-panel"
                  onClick={() => filterDialogRef.current?.showModal()}
                >
                  <SlidersHorizontal size={19} aria-hidden="true" />
                  <span className={styles.filterToggleLabel}>Filtros</span>
                  {activeFilterCount > 0 && <span className={styles.filterCount} aria-hidden="true">{activeFilterCount}</span>}
                </button>
              </div>
              <dialog ref={filterDialogRef} id="marketplace-filter-panel" className={styles.filterDialog} aria-labelledby="marketplace-filter-title">
                <div className={styles.filterPanelHeading}>
                  <h2 id="marketplace-filter-title">Filtros do Marketplace</h2>
                  <button type="button" className={styles.filterToggle} aria-label="Fechar filtros" onClick={() => filterDialogRef.current?.close()}><X size={19} aria-hidden="true" /></button>
                </div>
              {(filter === "products" ||
                filter === "services") &&
                (categories.length > 0 || selectedCategory) && (
                  <div
                    className={
                      styles.categoryFilters
                    }
                  >
                    <span
                      className={
                        styles.categoryLabel
                      }
                    >
                      Categorias
                    </span>

                    <div
                      className={
                        styles.categoryChips
                      }
                    >
                      <button
                        type="button"
                        className={
                          selectedCategory === ""
                            ? styles.categoryChipActive
                            : styles.categoryChip
                        }
                        onClick={() =>
                          setSelectedCategory("")
                        }
                      >
                        Todas
                      </button>

                      {[...new Set([selectedCategory, ...categories].filter(Boolean))].map(
                        (category) => (
                          <button
                            key={category}
                            type="button"
                            className={
                              selectedCategory ===
                              category
                                ? styles
                                    .categoryChipActive
                                : styles
                                    .categoryChip
                            }
                            onClick={() =>
                              setSelectedCategory(
                                category,
                              )
                            }
                          >
                            {category}
                          </button>
                        ),
                      )}
                    </div>
                  </div>
                )}

          <form onSubmit={(event) => { submitSearch(event); filterDialogRef.current?.close(); }} aria-label="Filtros do Marketplace">
            <div className={styles.filterPanelGrid}>
              <label className={styles.filterTextQuery}><span>Busca textual</span><input type="search" value={inputValue} onChange={(event) => setInputValue(event.target.value)} placeholder="Produto, serviço ou empresa" /></label>
              <label><span>Ordenação</span><select value={sort} onChange={(event) => setSort(event.target.value)}>
                <option value="">Mais relevantes / padrão</option><option value="recent">Mais recentes</option><option value="most_viewed">Mais acessados</option><option value="price_asc">Menor preço</option><option value="price_desc">Maior preço</option>
              </select></label>
              <label><span>Cidade</span><input value={city} onChange={(event) => setCity(event.target.value)} placeholder="Todas as cidades" /></label>
              <label><span>UF</span><select value={state} onChange={(event) => setState(event.target.value)}><option value="">Todos os estados</option>{["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"].map((uf) => <option key={uf} value={uf}>{uf}</option>)}</select></label>
                 <label><span>Preço mínimo</span><input type="number" min="0" step="0.01" inputMode="decimal" value={vehicleMinPrice} onChange={(event) => setVehicleMinPrice(event.target.value)} placeholder="Sem mínimo" /></label>
                <label><span>Preço máximo</span><input type="number" min="0" step="0.01" inputMode="decimal" value={vehicleMaxPrice} onChange={(event) => setVehicleMaxPrice(event.target.value)} placeholder="Sem máximo" /></label>
              {(filter === "products" || filter === "services") && subcategories.length > 0 && <label><span>Subcategoria</span><select value={subcategory} onChange={(event) => setSubcategory(event.target.value)}><option value="">Todas as subcategorias</option>{subcategories.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>}
            </div>
            <div className={styles.vehicleSearchFooter}><button type="submit">Aplicar filtros</button><button type="button" onClick={() => { clearFilters(); filterDialogRef.current?.close(); }}>Limpar filtros</button></div>
          </form>
              </dialog>
            </>
          )}

          {loading ? (
            <div className={styles.state}>
              <LoaderCircle
                className={styles.spinner}
                size={34}
              />
              <p>Carregando anúncios...</p>
            </div>
          ) : error ? (
            <div className={styles.state}>
              <p>{error}</p>
              <button
                type="button"
                onClick={() => void loadItems()}
              >
                Tentar novamente
              </button>
            </div>
          ) : filter === "vehicles" ? (
            renderVehicleMarketplace()
          ) : filter === "real_estate" ? (
            renderRealEstateMarketplace()
          ) : isMarketplaceHome ? (
            <div className={styles.sections}>
              {renderSection(
                "Mais acessados",
                homeSections.mostViewed,
                undefined,
                "Os anúncios que mais despertaram interesse.",
              )}

              {renderSection(
                "Destaques",
                homeSections.featured,
                undefined,
                "Uma seleção para você explorar.",
              )}

              {renderNovidades()}

              {renderSection(
                "Veículos",
                homeSections.vehicles,
                "vehicles",
              )}

              {renderSection(
                "Imóveis",
                homeSections.realEstate,
                "real_estate",
              )}

              {renderSection(
                "Produtos",
                homeSections.products,
                "products",
              )}

              {renderSection(
                "Serviços",
                homeSections.services,
                "services",
              )}
            </div>
          ) : (
            <>
              {items.length === 0 ? (
                <div className={styles.state}>
                  <Search size={38} />
                  <h3>
                    Nenhum anúncio encontrado
                  </h3>
                  <p>
                    Tente alterar a busca ou escolher
                    outra categoria.
                  </p>
                </div>
              ) : (
                <div className={styles.grid}>
                  {items.map((item) => {
                    const location =
                      getLocation(item);

                    return (
                      <MarketplaceTrackedLink
                        key={item.id}
                        item={item}
                        className={styles.card}
                      >
                        <div
                          className={
                            styles.imageWrap
                          }
                        >
                          {item.primary_image_url ? (
                            <img
                              src={
                                item.primary_image_url
                              }
                              alt={item.name}
                              loading="lazy"
                            />
                          ) : (
                            <div
                              className={
                                styles
                                  .imagePlaceholder
                              }
                            >
                              <ShoppingBag
                                size={34}
                              />
                            </div>
                          )}

                          <span
                            className={
                              styles.favoriteVisual
                            }
                            aria-hidden="true"
                          >
                            <Heart size={18} />
                          </span>

                          <span
                            className={
                              styles.typeBadge
                            }
                          >
                            {getItemLabel(
                              item.marketplace_type,
                            )}
                          </span>
                        </div>

                        <div
                          className={
                            styles.cardBody
                          }
                        >
                          <div
                            className={
                              styles.company
                            }
                          >
                            <span>
                              {
                                item.company
                                  .trade_name
                              }
                            </span>

                            {item.company
                              .is_verified && (
                              <span
                                className={
                                  styles.verified
                                }
                                title="Empresa verificada"
                              >
                                ✓
                              </span>
                            )}
                          </div>

                          <h3>{item.name}</h3>

                          {item.reference_code && (
                            <span
                              className={
                                styles.reference
                              }
                            >
                              Ref.{" "}
                              {
                                item.reference_code
                              }
                            </span>
                          )}

                          <div
                            className={
                              styles.price
                            }
                          >
                            {formatCurrency(
                              item.current_price,
                            )}
                          </div>

                          {location && (
                            <div
                              className={
                                styles.location
                              }
                            >
                              <MapPin size={15} />
                              <span>
                                {location}
                              </span>
                            </div>
                          )}
                        </div>
                      </MarketplaceTrackedLink>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}
